import { useMemo } from 'react';

import { useGetKidMarker } from '../../../../../api/kids/hooks/queries';
import type { KidMarker } from '../../../../../api/kids/types';

/** COST markers for composition-of-costs; FIG (+ FUND) for figure-based preset tables. */
export type PresetMarkerSource = 'cost' | 'figure';

export interface PresetKidMarkersContext {
  clientId?: string;
  fundId?: string;
  shareClassId?: string;
  figureCalculationDate?: string;
  overviewLastUpdatingDatetime?: string;
  /**
   * When set (e.g. after Refresh Figures), use these instead of fetching.
   * Keeps set-table / SRI seeding in sync with the markers just applied to the form.
   */
  markersOverride?: KidMarker[];
}

interface UsePresetKidMarkersResult {
  markers: KidMarker[];
  isLoading: boolean;
}

function filterMarkersForSource(
  markers: KidMarker[],
  source: PresetMarkerSource,
): KidMarker[] {
  if (source === 'cost') {
    return markers.filter((m) => m.marker_type === 'COST');
  }
  return markers.filter(
    (m) => m.marker_type === 'FIG' || m.marker_type === 'FUND',
  );
}

/**
 * Fetches KID markers for preset table blocks via `GET /kid/marker/type/...`.
 * Cost tables use COST; figure tables merge FIG and FUND marker types.
 */
export function usePresetKidMarkers(
  {
    clientId,
    fundId,
    shareClassId,
    figureCalculationDate,
    overviewLastUpdatingDatetime,
    markersOverride,
  }: PresetKidMarkersContext,
  source: PresetMarkerSource,
): UsePresetKidMarkersResult {
  const hasOverride = markersOverride != null && markersOverride.length > 0;
  const enabled = !!clientId && !!fundId && !!shareClassId && !hasOverride;

  const costQuery = useGetKidMarker(
    clientId,
    fundId,
    shareClassId,
    'COST',
    figureCalculationDate,
    overviewLastUpdatingDatetime,
    true,
    { enabled: enabled && source === 'cost' },
  );

  const figQuery = useGetKidMarker(
    clientId,
    fundId,
    shareClassId,
    'FIG',
    figureCalculationDate,
    overviewLastUpdatingDatetime,
    true,
    { enabled: enabled && source === 'figure' },
  );

  const fundQuery = useGetKidMarker(
    clientId,
    fundId,
    shareClassId,
    'FUND',
    figureCalculationDate,
    overviewLastUpdatingDatetime,
    true,
    { enabled: enabled && source === 'figure' },
  );

  const markers = useMemo(() => {
    if (hasOverride) {
      return filterMarkersForSource(markersOverride!, source);
    }
    if (source === 'cost') {
      return costQuery.data ?? [];
    }
    return [...(figQuery.data ?? []), ...(fundQuery.data ?? [])];
  }, [
    costQuery.data,
    figQuery.data,
    fundQuery.data,
    hasOverride,
    markersOverride,
    source,
  ]);

  const isLoading =
    hasOverride
      ? false
      : source === 'cost'
        ? costQuery.isLoading
        : figQuery.isLoading || fundQuery.isLoading;

  return { markers, isLoading };
}
